import React, { useState } from 'react';
import { Layout, Card, Typography, Row, Col, Button, Space, notification, Alert } from 'antd';
import { FileExcelOutlined, CloudDownloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { FileUploader, SummaryCard } from '../../components/index.js';
import { 
  parseWellskyFile, 
  uploadWellskyFile,
  fetchHubspotDeals, 
  getWellskyStats, 
  getHubspotStats 
} from '../../services/index.js';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const ImportPage = ({ onDataImported }) => {
  const [wellskyData, setWellskyData] = useState(null);
  const [hubspotData, setHubspotData] = useState(null);
  const [wellskyStats, setWellskyStats] = useState(null);
  const [hubspotStats, setHubspotStats] = useState(null);
  const [hubspotLoading, setHubspotLoading] = useState(false);

  const handleWellskyUpload = async (file) => {
    try {
      // Upload to backend and save to database
      const uploadResult = await uploadWellskyFile(file);
      const { records, saveResults } = uploadResult;
      
      // Also parse locally for immediate display
      const localData = await parseWellskyFile(file);
      const stats = getWellskyStats(localData);
      
      setWellskyData(localData);
      setWellskyStats({
        ...stats,
        saved: {
          created: saveResults.created,
          updated: saveResults.updated,
          errors: saveResults.errors,
          totalChanges: saveResults.totalChanges
        }
      });
      
      notification.success({
        message: 'WellSky Data Imported',
        description: `Successfully processed ${records.length} records (${saveResults.created} new, ${saveResults.updated} updated) and saved to database`,
        placement: 'topRight'
      });

      // Notify parent component if both datasets are ready
      if (hubspotData) {
        onDataImported && onDataImported({ wellsky: localData, hubspot: hubspotData });
      }

      return localData;
    } catch (error) {
      notification.error({
        message: 'WellSky Import Failed',
        description: error.message,
        placement: 'topRight'
      });
      throw error;
    }
  };

  const handleHubspotImport = async () => {
    const token = localStorage.getItem('hubspot_api_token');
    
    if (!token) {
      notification.error({
        message: 'HubSpot Token Required',
        description: 'Please configure your HubSpot API token in Settings first.',
        placement: 'topRight'
      });
      return;
    }

    setHubspotLoading(true);
    
    try {
      const result = await fetchHubspotDeals(token);
      
      // The fetchHubspotDeals now returns an object with deals, saveResults, etc.
      let data, saveResults;
      if (Array.isArray(result)) {
        // Backward compatibility if it returns just an array
        data = result;
        saveResults = null;
      } else {
        // New format with save results
        data = result.deals || result;
        saveResults = result.saveResults;
      }
      
      const stats = getHubspotStats(data);
      
      setHubspotData(data);
      setHubspotStats({
        ...stats,
        saved: saveResults ? {
          created: saveResults.created,
          updated: saveResults.updated,
          errors: saveResults.errors,
          totalChanges: saveResults.totalChanges
        } : null
      });
      
      const saveMessage = saveResults 
        ? ` (${saveResults.created} new, ${saveResults.updated} updated, saved to database)`
        : '';
      
      notification.success({
        message: 'HubSpot Data Imported',
        description: `Successfully loaded ${data.length} deals from HubSpot${saveMessage}`,
        placement: 'topRight'
      });

      // Notify parent component if both datasets are ready
      if (wellskyData) {
        onDataImported && onDataImported({ wellsky: wellskyData, hubspot: data });
      }

    } catch (error) {
      notification.error({
        message: 'HubSpot Import Failed',
        description: error.message,
        placement: 'topRight'
      });
    } finally {
      setHubspotLoading(false);
    }
  };

  const handleClearData = () => {
    setWellskyData(null);
    setHubspotData(null);
    setWellskyStats(null);
    setHubspotStats(null);
    
    notification.info({
      message: 'Data Cleared',
      description: 'All imported data has been cleared. You can import new data.',
      placement: 'topRight'
    });
  };

  const isDataReady = wellskyData && hubspotData;

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Content style={{ padding: '24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Success Alert */}
          {isDataReady && (
            <Alert
              message="Data Import Complete"
              description="Both WellSky and HubSpot data have been imported successfully. You can now proceed to preview the data or start matching."
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 24 }}
              action={
                <Space>
                  <Button size="small" onClick={handleClearData}>
                    Clear All Data
                  </Button>
                </Space>
              }
            />
          )}

          {/* Import Cards */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            {/* WellSky Import */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <FileExcelOutlined />
                    WellSky Data Import
                  </Space>
                }
                style={{ height: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Paragraph>
                      Upload a WellSky Excel file containing client data. The file should include 
                      the &quot;Wellsky Raw Data&quot; sheet with all required columns.
                    </Paragraph>
                    <Paragraph type="secondary" style={{ fontSize: 12 }}>
                      <strong>Expected columns:</strong> Database ID, Full Name, Agency Subdomain, 
                      Address, Phone Numbers, Email, Start Date, etc.
                    </Paragraph>
                  </div>

                  <FileUploader
                    onParse={handleWellskyUpload}
                    accepted=".xlsx,.xls,.csv"
                    title="Upload WellSky File"
                    description="Select an Excel or CSV file containing WellSky client data"
                    maxSize={50}
                  />

                  {wellskyStats && (
                    <div>
                      <Title level={5}>Import Summary</Title>
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <SummaryCard
                            title="Total Records"
                            value={wellskyStats.total}
                            size="small"
                          />
                        </Col>
                        <Col span={12}>
                          <SummaryCard
                            title="Valid Records"  
                            value={wellskyStats.valid}
                            size="small"
                            valueStyle={{ color: '#52c41a' }}
                          />
                        </Col>
                        <Col span={12}>
                          <SummaryCard
                            title="With Warnings"
                            value={wellskyStats.withWarnings}
                            size="small"
                            valueStyle={{ color: '#faad14' }}
                          />
                        </Col>
                        <Col span={12}>
                          <SummaryCard
                            title="Errors"
                            value={wellskyStats.errors}
                            size="small"
                            valueStyle={{ color: '#ff4d4f' }}
                          />
                        </Col>
                      </Row>
                      
                      {/* Database Save Results */}
                      {wellskyStats.saved && (
                        <>
                          <Title level={5} style={{ marginTop: 16 }}>Database Save Results</Title>
                          <Row gutter={[8, 8]}>
                            <Col span={12}>
                              <SummaryCard
                                title="New Records"
                                value={wellskyStats.saved.created}
                                size="small"
                                valueStyle={{ color: '#52c41a' }}
                              />
                            </Col>
                            <Col span={12}>
                              <SummaryCard
                                title="Updated Records"
                                value={wellskyStats.saved.updated}
                                size="small"
                                valueStyle={{ color: '#1890ff' }}
                              />
                            </Col>
                            <Col span={12}>
                              <SummaryCard
                                title="Total Changes"
                                value={wellskyStats.saved.totalChanges}
                                size="small"
                                valueStyle={{ color: '#722ed1' }}
                              />
                            </Col>
                            <Col span={12}>
                              <SummaryCard
                                title="Save Errors"
                                value={wellskyStats.saved.errors}
                                size="small"
                                valueStyle={{ color: '#ff4d4f' }}
                              />
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  )}
                </Space>
              </Card>
            </Col>

            {/* HubSpot Import */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <CloudDownloadOutlined />
                    HubSpot Data Import
                  </Space>
                }
                style={{ height: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Paragraph>
                      Import deal data directly from HubSpot using the API. This will fetch 
                      all deals with their associated properties and contact information.
                    </Paragraph>
                    <Paragraph type="secondary" style={{ fontSize: 12 }}>
                      <strong>Note:</strong> Requires a valid HubSpot API token configured in Settings.
                    </Paragraph>
                  </div>

                  <Button 
                    type="primary"
                    icon={<CloudDownloadOutlined />}
                    loading={hubspotLoading}
                    onClick={handleHubspotImport}
                    size="large"
                    block
                  >
                    {hubspotLoading ? 'Fetching HubSpot Data...' : 'Fetch HubSpot Data'}
                  </Button>

                  {hubspotStats && (
                    <div>
                      <Title level={5}>Import Summary</Title>
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <SummaryCard
                            title="Total Deals"
                            value={hubspotStats.total}
                            size="small"
                          />
                        </Col>
                        <Col span={12}>
                          <SummaryCard
                            title="Valid Deals"
                            value={hubspotStats.valid}
                            size="small"
                            valueStyle={{ color: '#52c41a' }}
                          />
                        </Col>
                        <Col span={12}>
                          <SummaryCard
                            title="Commission Eligible"
                            value={hubspotStats.commissionEligible}
                            size="small"
                            valueStyle={{ color: '#1890ff' }}
                          />
                        </Col>
                        <Col span={12}>
                          <SummaryCard
                            title="With Warnings"
                            value={hubspotStats.withWarnings}
                            size="small"
                            valueStyle={{ color: '#faad14' }}
                          />
                        </Col>
                      </Row>
                      
                      {/* Database Save Results */}
                      {hubspotStats.saved && (
                        <>
                          <Title level={5} style={{ marginTop: 16 }}>Database Save Results</Title>
                          <Row gutter={[8, 8]}>
                            <Col span={12}>
                              <SummaryCard
                                title="New Deals"
                                value={hubspotStats.saved.created}
                                size="small"
                                valueStyle={{ color: '#52c41a' }}
                              />
                            </Col>
                            <Col span={12}>
                              <SummaryCard
                                title="Updated Deals"
                                value={hubspotStats.saved.updated}
                                size="small"
                                valueStyle={{ color: '#1890ff' }}
                              />
                            </Col>
                            <Col span={12}>
                              <SummaryCard
                                title="Total Changes"
                                value={hubspotStats.saved.totalChanges}
                                size="small"
                                valueStyle={{ color: '#722ed1' }}
                              />
                            </Col>
                            <Col span={12}>
                              <SummaryCard
                                title="Save Errors"
                                value={hubspotStats.saved.errors}
                                size="small"
                                valueStyle={{ color: '#ff4d4f' }}
                              />
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Instructions */}
          <Card title="Import Instructions">
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <Title level={5}>WellSky File Requirements</Title>
                <ul>
                  <li>Excel (.xlsx, .xls) or CSV file format</li>
                  <li>Sheet named &quot;Wellsky Raw Data&quot; (or first sheet)</li>
                  <li>Required columns: Database ID, Full Name, Agency Subdomain</li>
                  <li>Optional columns: Address, Phone, Email, Start Date, etc.</li>
                  <li>Remove any placeholder or search rows</li>
                </ul>
              </Col>
              <Col xs={24} md={12}>
                <Title level={5}>HubSpot API Requirements</Title>
                <ul>
                  <li>Valid HubSpot API token with deal read permissions</li>
                  <li>Access to deal properties and custom fields</li>
                  <li>Network connection to api.hubspot.com</li>
                  <li>Sufficient API rate limits for your account</li>
                </ul>
              </Col>
            </Row>
          </Card>
        </div>
      </Content>
    </Layout>
  );
};

export default ImportPage;
